import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { FileText, Printer, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface LayawayInvoiceProps {
  layaway: any;
  currencySymbol?: string;
  vatPercentage?: number;
  branchName?: string;
  companyDetails?: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
}

export const LayawayInvoice: React.FC<LayawayInvoiceProps> = ({
  layaway,
  currencySymbol = '$',
  vatPercentage = 0,
  branchName = '',
  companyDetails = {
    name: 'Vixalor POS',
    address: '123 Business Street',
    phone: '+1234567890',
    email: 'info@vixalor.com'
  }
}) => {
  const invoiceRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;
    
    const canvas = await html2canvas(invoiceRef.current);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF();
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    
    let position = 0;
    
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    pdf.save(`layaway-${layaway.layaway_number}.pdf`);
  };

  const subtotal = layaway.items?.reduce((sum: number, item: any) => 
    sum + (parseFloat(item.price) * item.quantity), 0) || 0;
  
  const vatAmount = layaway.items?.reduce((sum: number, item: any) => {
    if (item.vat_enabled) {
      const itemTotal = parseFloat(item.price) * item.quantity;
      return sum + (itemTotal * (vatPercentage / 100));
    }
    return sum;
  }, 0) || 0;

  const total = subtotal + vatAmount;
  const paidAmount = layaway.paid_amount || 0;
  const remainingAmount = layaway.remaining_amount || (total - paidAmount);

  return (
    <>
      <div className="flex justify-end gap-2 mb-4 print:hidden">
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Print
        </Button>
        <Button variant="outline" onClick={handleDownloadPDF}>
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
      </div>

      <div ref={invoiceRef} className="bg-white p-8 rounded-lg shadow-sm">
        {/* Header */}
        <div className="flex justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{companyDetails.name}</h1>
            <p className="text-gray-600">{companyDetails.address}</p>
            <p className="text-gray-600">Phone: {companyDetails.phone}</p>
            <p className="text-gray-600">Email: {companyDetails.email}</p>
            {branchName && <p className="text-gray-600">Branch: {branchName}</p>}
          </div>
          <div className="text-right">
            <h2 className="text-xl font-semibold text-gray-800">LAYAWAY INVOICE</h2>
            <p className="text-gray-600">#{layaway.layaway_number}</p>
            <p className="text-gray-600">Date: {new Date(layaway.created_at).toLocaleDateString()}</p>
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
              layaway.status === 'active' ? 'bg-blue-100 text-blue-800' :
              layaway.status === 'completed' ? 'bg-green-100 text-green-800' :
              'bg-red-100 text-red-800'
            }`}>
              {layaway.status.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="mb-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-2">Customer Details</h3>
          <p className="text-gray-600">Name: {layaway.customer_name}</p>
          {layaway.customer_phone && <p className="text-gray-600">Phone: {layaway.customer_phone}</p>}
          {layaway.customer_address && <p className="text-gray-600">Address: {layaway.customer_address}</p>}
        </div>

        {/* Layaway Details */}
        <div className="mb-8 grid grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-2">Layaway Details</h3>
            <p className="text-gray-600">Product Handling: {layaway.product_handling === 'hold' ? 'Hold in Store' : 'Handed Over'}</p>
            <p className="text-gray-600">Payment Schedule: {layaway.payment_schedule_months} months</p>
            <p className="text-gray-600">Created By: {layaway.user?.name || 'N/A'}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-2">Payment Summary</h3>
            <p className="text-gray-600">Total Amount: {currencySymbol}{total.toFixed(2)}</p>
            <p className="text-green-600">Paid Amount: {currencySymbol}{paidAmount.toFixed(2)}</p>
            <p className="text-red-600">Remaining: {currencySymbol}{remainingAmount.toFixed(2)}</p>
            <p className="text-gray-600">Monthly Payment: {currencySymbol}{(remainingAmount / (layaway.payment_schedule_months || 1)).toFixed(2)}</p>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-8">
          <h3 className="font-semibold text-gray-700 mb-4">Items</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-2">Item</th>
                <th className="text-left py-2">SKU</th>
                <th className="text-center py-2">Qty</th>
                <th className="text-right py-2">Price</th>
                <th className="text-right py-2">VAT</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {layaway.items?.map((item: any, index: number) => {
                const itemTotal = parseFloat(item.price) * item.quantity;
                const itemVat = item.vat_enabled ? (itemTotal * (vatPercentage / 100)) : 0;
                return (
                  <tr key={index} className="border-b border-gray-200">
                    <td className="py-2">{item.name}</td>
                    <td className="py-2">{item.sku}</td>
                    <td className="text-center py-2">{item.quantity}</td>
                    <td className="text-right py-2">{currencySymbol}{parseFloat(item.price).toFixed(2)}</td>
                    <td className="text-right py-2">{itemVat > 0 ? `${currencySymbol}${itemVat.toFixed(2)}` : '-'}</td>
                    <td className="text-right py-2">{currencySymbol}{(itemTotal + itemVat).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300">
                <td colSpan={5} className="text-right py-2 font-semibold">Subtotal:</td>
                <td className="text-right py-2 font-semibold">{currencySymbol}{subtotal.toFixed(2)}</td>
              </tr>
              {vatAmount > 0 && (
                <tr>
                  <td colSpan={5} className="text-right py-2">VAT ({vatPercentage}%):</td>
                  <td className="text-right py-2">{currencySymbol}{vatAmount.toFixed(2)}</td>
                </tr>
              )}
              <tr className="text-lg font-bold">
                <td colSpan={5} className="text-right py-2">Total:</td>
                <td className="text-right py-2">{currencySymbol}{total.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Payment History */}
        {layaway.payments && layaway.payments.length > 0 && (
          <div className="mb-8">
            <h3 className="font-semibold text-gray-700 mb-4">Payment History</h3>
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Amount</th>
                  <th className="text-left py-2">Method</th>
                  <th className="text-left py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {layaway.payments.map((payment: any, index: number) => (
                  <tr key={index} className="border-b border-gray-200">
                    <td className="py-2">{new Date(payment.payment_date).toLocaleDateString()}</td>
                    <td className="py-2">{currencySymbol}{parseFloat(payment.amount).toFixed(2)}</td>
                    <td className="py-2">{payment.payment_method}</td>
                    <td className="py-2">{payment.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {layaway.notes && (
          <div className="mb-8 p-4 bg-yellow-50 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-2">Notes</h3>
            <p className="text-gray-600">{layaway.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8 pt-4 border-t">
          <p>Thank you for your business!</p>
          <p>This is a computer-generated document. No signature is required.</p>
        </div>
      </div>
    </>
  );
};